/**
 * Contas na corretora (spec 043, secao 4): as contas dos planos da Global com o que a corretora diz delas (estado pela
 * API, saldo pela conexao), nas abas A resolver, Ativas e Todas; no topo, se a leitura e a conexao estao de pe. E a
 * conta na ficha da matricula. So leitura: nada aqui escreve na corretora. Quem decide estado, saldo, resultado e o que
 * esta "a resolver" e o backend; a tela so mostra. Acende pelo modulo `nelogica-v2` (e o saldo pelo `saldo`).
 */
import {
  Abas, Aviso, Botao, CabecalhoDePagina, Carregando, ESTADO_NA_CORRETORA, Fatos, Grade, LinkInterno, Pilha, SaldoNaCorretora, Secao, Selo, Tabela, Vazio,
  formatarDataHora, formatarDia, formatarMoeda, numerosDoSaldo, tempoRelativo, type Fato, type MotivoSemSaldo, type SituacaoDaConta,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { STATUS_DA_MATRICULA, erroDe, useAcao, useApi, usePapel, type ModuloContaNaCorretora, type ModuloSaldo, type StatusMatricula } from "../api";
import { useAmbiente } from "../contexto";

/** Por que nao ha saldo, na lingua da equipe (o cliente le outra frase, mais curta). */
export const SEM_SALDO_NO_PAINEL: Record<MotivoSemSaldo, string> = {
  LEITURA_DESLIGADA: "Conexão de saldo desligada neste ambiente.",
  AINDA_NAO_VISTA: "A conexão ainda não trouxe o saldo desta conta.",
  SEM_CONTA: "Sem conta declarada: o saldo aparece depois da liberação.",
  PLANO_ENCERRADO: "Plano encerrado: o saldo da conta não é mais deste plano.",
};

const simOuNao = (v: boolean | null | undefined) => (v == null ? "Não informado" : v ? "Sim" : "Não");

/** O bloqueio por risco que a conexao viu (3.11), com a frase da corretora. Nao vira pendencia: decidir e do administrador. */
function situacoesDoRisco(s: ModuloSaldo | undefined): SituacaoDaConta[] {
  if (!s?.bloqueada_por_risco) return [];
  const frase = s.motivo_do_risco?.trim();
  return [{ tom: "erro", titulo: frase ? `Bloqueada por risco: ${frase}` : "Bloqueada por risco", detalhe: s.risco_visto_em ? `Visto em ${formatarDataHora(s.risco_visto_em)}` : undefined }];
}

/** O saldo do resumo pronto pro bloco, com a frase do painel quando nao ha saldo. */
function SaldoDoPainel({ s, situacoes }: { s: ModuloSaldo; situacoes?: SituacaoDaConta[] }) {
  const { saldo, inicial, resultado } = numerosDoSaldo(s);
  return (
    <SaldoNaCorretora
      situacoes={situacoes}
      saldo={saldo}
      semSaldo={s.motivo_sem_saldo ? SEM_SALDO_NO_PAINEL[s.motivo_sem_saldo] : undefined}
      inicial={s.motivo_sem_saldo === "PLANO_ENCERRADO" ? undefined : inicial}
      semInicial="Não definido no plano"
      resultado={resultado}
    />
  );
}

/**
 * A conta na ficha da matricula (spec 043): o numero, quem declarou, bloqueada e desativada separados, desde quando, as
 * datas das leituras, o risco e o saldo com a idade, o inicial e o resultado.
 */
export function ContaNaCorretoraDaMatricula({ corretora, saldo }: { corretora?: ModuloContaNaCorretora; saldo?: ModuloSaldo }) {
  const c = corretora;
  const estado = c?.estado ? ESTADO_NA_CORRETORA[c.estado] : null;
  const fatos: Fato[] = c
    ? [
        { rotulo: "Conta", valor: c.conta, mono: true, detalhe: c.vinculada_em ? `Declarada${c.declarada_por ? ` por ${c.declarada_por}` : ""} em ${formatarDataHora(c.vinculada_em)}` : undefined },
        // Spec 044: a etapa e registro (Challenge ou Funded), corrigido no "Gerenciar plano".
        ...(c.etapa_rotulo ? [{ rotulo: "Etapa", valor: c.etapa_rotulo, detalhe: c.etapa_desde ? `desde ${formatarDia(c.etapa_desde)}` : undefined }] : []),
        ...(c.estado === null
          ? [{ rotulo: "Na corretora", valor: "Plano encerrado", detalhe: "A conta pode estar com outro plano agora." }]
          : c.estado === "LEITURA_DESLIGADA"
          ? [{ rotulo: "Na corretora", valor: "Leitura desligada", detalhe: "Sem as credenciais da API neste ambiente, ninguém aqui sabe se a conta está ativa." }]
          : [
              { rotulo: "Bloqueada", valor: simOuNao(c.bloqueada) },
              { rotulo: "Desativada", valor: simOuNao(c.desativada) },
              ...(c.bloqueada_desde ? [{ rotulo: "Bloqueada desde", valor: formatarDataHora(c.bloqueada_desde) }] : []),
              { rotulo: "Última leitura", valor: c.lida_em ? formatarDataHora(c.lida_em) : "Nunca lida", detalhe: c.lida_em ? tempoRelativo(c.lida_em) : undefined },
              { rotulo: "Confirmada em", valor: c.confirmada_em ? formatarDataHora(c.confirmada_em) : "Nunca", detalhe: "Última leitura que trouxe o estado da conta" },
            ]),
        // Bloqueada por risco vem em destaque junto do saldo; aqui so o "nao bloqueada" ou "sem leitura" do risco.
        ...(saldo && c.estado !== null && !saldo.bloqueada_por_risco ? [{ rotulo: "Risco", valor: saldo.bloqueada_por_risco === false ? "Sem bloqueio" : "Sem leitura", detalhe: saldo.risco_visto_em ? `Visto em ${formatarDataHora(saldo.risco_visto_em)}` : undefined }] : []),
      ]
    : [];
  return (
    <Secao rotulo="Corretora" titulo="Conta na corretora" acoes={estado ? <Selo tom={estado.tom}>{estado.rotulo}</Selo> : undefined}>
      {c ? <Fatos itens={fatos} /> : <p className="mtc-texto-2" style={{ margin: 0 }}>Sem conta declarada. O número entra na liberação.</p>}
      {/* Sem conta, o saldo so repetiria "sem conta": a frase de cima ja diz. */}
      {saldo && c && <SaldoDoPainel s={saldo} situacoes={c.estado === null ? [] : situacoesDoRisco(saldo)} />}
    </Secao>
  );
}

// ---------------------------------------------------------------- a tela

type Aba = "A_RESOLVER" | "ATIVAS" | "TODAS";
const ABAS: Aba[] = ["A_RESOLVER", "ATIVAS", "TODAS"];
type LinhaDaConta = { matricula_id: string; status: StatusMatricula; ativada_em: string | null; pessoa: { id: string; nome: string }; plano: { slug: string; nome: string }; corretora: ModuloContaNaCorretora | null; saldo: ModuloSaldo | null };
type ListaDeContas = { contas: LinhaDaConta[]; contagem: { a_resolver: number; ativas: number; todas: number } };
type Leitura = { configurada: boolean; ultima_em: string | null; proxima_em: string | null; ultimo_erro: string | null; resultado: Record<string, unknown> | null; contas_na_corretora: number };
type Conexao = { configurada: boolean; ligada: boolean; conectada: boolean; token_recusado?: boolean; ultima_foto_em?: string | null; ultima_mensagem_em?: string | null; contas_vistas: number; ultima_leitura_em: string | null };
type SaldoInicialDoPlano = { plano: string; nome: string; ativo: boolean; saldo_inicial_centavos: number | null; moeda: string | null; atualizado_em: string | null };

/** O que a rodada da leitura devolveu, numa frase. As chaves sao as do backend; chave nova so nao aparece. */
export function fraseDaLeitura(r: Record<string, unknown> | null | undefined): string {
  if (!r) return "Leitura feita.";
  if (r.desligada) return "A leitura da corretora está desligada neste ambiente.";
  if (r.ja_rodando) return "Já havia uma leitura em andamento. Os dados chegam em instantes.";
  const n = (k: string) => (typeof r[k] === "number" ? (r[k] as number) : null);
  const partes = [
    n("contas_lidas") != null ? `${n("contas_lidas")} ${n("contas_lidas") === 1 ? "conta lida" : "contas lidas"}` : null,
    n("nao_encontradas") ? `${n("nao_encontradas")} não ${n("nao_encontradas") === 1 ? "encontrada" : "encontradas"}` : null,
    n("bloqueios_avisados") ? `${n("bloqueios_avisados")} ${n("bloqueios_avisados") === 1 ? "bloqueio novo no sino" : "bloqueios novos no sino"}` : null,
    n("bloqueios_resolvidos") ? `${n("bloqueios_resolvidos")} ${n("bloqueios_resolvidos") === 1 ? "bloqueio resolvido" : "bloqueios resolvidos"}` : null,
  ].filter(Boolean);
  return partes.length ? `Leitura feita: ${partes.join(", ")}.` : "Leitura feita.";
}

export function PaginaContasNaCorretora() {
  const { ler, postar } = useApi();
  const { pode } = usePapel();
  const { modulos } = useAmbiente();
  const comSaldo = modulos.includes("saldo");
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const aba = (ABAS.includes(params.get("aba") as Aba) ? params.get("aba") : "A_RESOLVER") as Aba;
  const mudarAba = (v: Aba) => { const p = new URLSearchParams(params); if (v === "A_RESOLVER") p.delete("aba"); else p.set("aba", v); setParams(p, { replace: true }); };
  const contas = useQuery({ queryKey: ["corretora", "contas", aba], queryFn: () => ler<ListaDeContas>(`/v1/admin/corretora/contas?aba=${aba}`) });
  const leitura = useQuery({ queryKey: ["corretora", "leitura"], queryFn: () => ler<Leitura>("/v1/admin/corretora/leitura") });
  const conexao = useQuery({ queryKey: ["corretora", "conexao"], queryFn: () => ler<Conexao>("/v1/admin/saldo/conexao"), enabled: comSaldo });
  const iniciais = useQuery({ queryKey: ["saldo-dos-planos"], queryFn: () => ler<SaldoInicialDoPlano[]>("/v1/admin/saldo/planos"), enabled: comSaldo });
  const lerAgora = useAcao();
  const [lido, setLido] = useState<string>();
  const c = contas.data?.contagem;
  const planosSemBase = (iniciais.data ?? []).filter((p) => p.ativo && p.saldo_inicial_centavos == null);
  const desligada = leitura.data && !leitura.data.configurada;

  const pedirLeitura = async () => {
    setLido(undefined);
    const ok = await lerAgora.rodar(async () => {
      const r = await postar<{ ok: boolean; resultado: Record<string, unknown> }>("/v1/admin/corretora/ler");
      setLido(fraseDaLeitura(r.resultado));
    });
    if (ok) await qc.invalidateQueries({ queryKey: ["corretora"] });
  };

  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina
        titulo="Contas na corretora"
        descricao="A conta de cada plano com o que a corretora diz dela: o estado e o saldo. Só leitura: nada daqui muda a conta na corretora. Clique numa linha pra abrir a matrícula."
        acoes={pode("ADMIN") && leitura.data?.configurada ? <Botao variante="secundario" carregando={lerAgora.carregando} onClick={() => void pedirLeitura()}>Ler agora</Botao> : undefined}
      />
      {lerAgora.erro && <Aviso tipo="erro">{lerAgora.erro}</Aviso>}
      {lido && <Aviso tipo="sucesso">{lido}</Aviso>}

      <Grade minimo={320}>
        <Secao rotulo="Estado da conta" titulo="Leitura da corretora" acoes={leitura.data ? <Selo tom={!leitura.data.configurada ? "neutro" : leitura.data.ultimo_erro ? "erro" : leitura.data.ultima_em ? "sucesso" : "alerta"}>{!leitura.data.configurada ? "Desligada" : leitura.data.ultimo_erro ? "Com erro" : leitura.data.ultima_em ? "Funcionando" : "Ainda não leu"}</Selo> : undefined}>
          {leitura.isLoading ? <Carregando compacto /> : leitura.isError ? <Aviso tipo="erro">{erroDe(leitura.error)}</Aviso> : leitura.data && (
            <>
              {!leitura.data.configurada && <p className="mtc-texto-2" style={{ margin: 0 }}>Leitura da corretora desligada neste ambiente. Sem as credenciais da API, ninguém aqui sabe se a conta está ativa ou bloqueada.</p>}
              {leitura.data.ultimo_erro && <Aviso tipo="erro">A última leitura falhou: {leitura.data.ultimo_erro}</Aviso>}
              {/* Desligada, a rotina roda e nao le nada: "ultima leitura ha 2 min" seria mentira. */}
              {leitura.data.configurada && (
                <Fatos
                  itens={[
                    { rotulo: "Última leitura", valor: leitura.data.ultima_em ? formatarDataHora(leitura.data.ultima_em) : "Nunca", detalhe: leitura.data.ultima_em ? tempoRelativo(leitura.data.ultima_em) : undefined },
                    ...(leitura.data.proxima_em ? [{ rotulo: "Próxima", valor: formatarDataHora(leitura.data.proxima_em) }] : []),
                    { rotulo: "Contas na corretora", valor: String(leitura.data.contas_na_corretora), mono: true, detalhe: "Todas as que a última leitura achou, com dono aqui ou não" },
                  ]}
                />
              )}
            </>
          )}
        </Secao>
        {comSaldo && (
          <Secao rotulo="Saldo" titulo="Conexão de saldo" acoes={conexao.data ? <Selo tom={!conexao.data.configurada ? "neutro" : conexao.data.token_recusado ? "erro" : conexao.data.conectada ? "sucesso" : "alerta"}>{!conexao.data.configurada ? "Desligada" : conexao.data.token_recusado ? "Token recusado" : conexao.data.conectada ? "Conectada" : "Reconectando"}</Selo> : undefined}>
            {conexao.isLoading ? <Carregando compacto /> : conexao.isError ? <Aviso tipo="erro">{erroDe(conexao.error)}</Aviso> : conexao.data && (
              <>
                {!conexao.data.configurada && <p className="mtc-texto-2" style={{ margin: 0 }}>Conexão de saldo desligada neste ambiente. O saldo que aparece é o último guardado, sempre com a hora em que foi visto.</p>}
                {conexao.data.token_recusado && <Aviso tipo="erro">A corretora recusou o token da conexão de saldo. A conexão parou de tentar: é preciso trocar o token no ambiente.</Aviso>}
                <Fatos
                  itens={[
                    { rotulo: "Último saldo recebido", valor: conexao.data.ultima_leitura_em ? formatarDataHora(conexao.data.ultima_leitura_em) : "Nunca", detalhe: conexao.data.ultima_leitura_em ? tempoRelativo(conexao.data.ultima_leitura_em) : undefined },
                    { rotulo: "Contas com saldo", valor: String(conexao.data.contas_vistas), mono: true, detalhe: "Vistas pela conexão, com dono aqui ou não" },
                    ...(conexao.data.configurada && conexao.data.ultima_foto_em ? [{ rotulo: "Última foto completa", valor: formatarDataHora(conexao.data.ultima_foto_em) }] : []),
                  ]}
                />
              </>
            )}
          </Secao>
        )}
      </Grade>

      {planosSemBase.length > 0 && (
        <Aviso tipo="alerta">
          {planosSemBase.length === 1 ? "Um plano ativo está" : `${planosSemBase.length} planos ativos estão`} sem saldo inicial: {planosSemBase.map((p) => p.nome).join(", ")}. O resultado das contas desses planos aparece como sem base pra calcular. Defina em <LinkInterno href="/planos">Planos</LinkInterno>.
        </Aviso>
      )}

      <Abas
        rotulo="Contas"
        valor={aba}
        aoMudar={mudarAba}
        abas={[
          { valor: "A_RESOLVER", rotulo: "A resolver", contagem: c?.a_resolver },
          { valor: "ATIVAS", rotulo: "Ativas", contagem: c?.ativas },
          { valor: "TODAS", rotulo: "Todas", contagem: c?.todas },
        ]}
      />
      {contas.isLoading ? <Carregando compacto texto="Buscando as contas" /> : contas.isError ? <Aviso tipo="erro">{erroDe(contas.error)}</Aviso> : (
        <Tabela
          linhas={contas.data?.contas ?? []}
          chave={(l) => l.matricula_id}
          aoClicar={(l) => navegar(`/matriculas/${l.matricula_id}`)}
          cartao="media"
          vazio={
            aba === "A_RESOLVER" ? (
              <Vazio titulo="Nada a resolver">{desligada ? "A leitura da corretora está desligada neste ambiente: sem leitura, nenhuma conta entra aqui. As contas estão em Todas." : "Nenhuma conta de plano ativo está bloqueada, desativada, não encontrada ou sem leitura."}</Vazio>
            ) : aba === "ATIVAS" ? (
              <Vazio titulo="Nenhuma conta ativa">{desligada ? "Sem a leitura da corretora, ninguém aqui sabe se a conta está ativa. As contas estão em Todas." : "Nenhuma conta de plano ativo foi confirmada como ativa pela corretora."}</Vazio>
            ) : (
              <Vazio titulo="Nenhuma conta ainda">As contas aparecem aqui quando o plano é liberado com o número da conta.</Vazio>
            )
          }
          colunas={[
            { chave: "cliente", rotulo: "Cliente", celula: (l) => <><LinkInterno href={`/traders/${l.pessoa.id}`}>{l.pessoa.nome}</LinkInterno>{l.status !== "ATIVA" && <span className="mtc-tabela__apoio">{STATUS_DA_MATRICULA[l.status].rotulo}</span>}</> },
            // A etapa (spec 044) vai junto do plano, num selo: a tabela ja tem colunas demais pra mais uma.
            { chave: "plano", rotulo: "Plano e etapa", celula: (l) => <><LinkInterno href={`/matriculas/${l.matricula_id}`}>{l.plano.nome}</LinkInterno>{l.corretora?.etapa_rotulo && <span className="mtc-linha" style={{ gap: "var(--e-1)", flexWrap: "wrap", marginTop: "var(--e-1)" }}><Selo tom={l.corretora.etapa === "FUNDED" ? "acento" : "neutro"}>{l.corretora.etapa_rotulo}</Selo>{l.corretora.etapa_desde && <span className="mtc-tabela__apoio">desde {formatarDia(l.corretora.etapa_desde)}</span>}</span>}</> },
            { chave: "conta", rotulo: "Conta", celula: (l) => <span className="mtc-mono">{l.corretora?.conta ?? ""}</span> },
            { chave: "estado", rotulo: "Estado", celula: (l) => <EstadoDaLinha l={l} /> },
            ...(comSaldo
              ? [
                  { chave: "saldo", rotulo: "Saldo", alinhar: "direita" as const, celula: (l: LinhaDaConta) => <SaldoDaLinha s={l.saldo} /> },
                  { chave: "resultado", rotulo: "Resultado", alinhar: "direita" as const, celula: (l: LinhaDaConta) => <ResultadoDaLinha s={l.saldo} /> },
                ]
              : []),
          ]}
        />
      )}
    </Pilha>
  );
}

function EstadoDaLinha({ l }: { l: LinhaDaConta }) {
  const e = l.corretora?.estado;
  const risco = l.saldo?.bloqueada_por_risco;
  if (!e) return <span className="mtc-texto-2">{l.status === "ENCERRADA" ? "Plano encerrado" : "Sem estado"}</span>;
  return (
    <span className="mtc-linha" style={{ gap: "var(--e-1)", flexWrap: "wrap" }}>
      <Selo tom={ESTADO_NA_CORRETORA[e].tom}>{ESTADO_NA_CORRETORA[e].rotulo}</Selo>
      {risco && <Selo tom="erro" titulo={l.saldo?.motivo_do_risco ? `Bloqueada por risco: ${l.saldo.motivo_do_risco}` : "Bloqueada por risco"}>Risco</Selo>}
      {e !== "ATIVA" && l.corretora?.bloqueada_desde && <span className="mtc-tabela__apoio">desde {formatarDataHora(l.corretora.bloqueada_desde)}</span>}
    </span>
  );
}

function SaldoDaLinha({ s }: { s: ModuloSaldo | null }) {
  if (!s) return <span className="mtc-texto-2">Sem saldo</span>;
  const { saldo } = numerosDoSaldo(s);
  if (!saldo) return <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{s.motivo_sem_saldo === "AINDA_NAO_VISTA" ? "Ainda não visto" : s.motivo_sem_saldo === "LEITURA_DESLIGADA" ? "Conexão desligada" : s.motivo_sem_saldo === "PLANO_ENCERRADO" ? "Plano encerrado" : "Sem conta"}</span>;
  return <><span className="mtc-mono">{saldo.valor}</span><span className="mtc-tabela__apoio">{saldo.idade}</span></>;
}

function ResultadoDaLinha({ s }: { s: ModuloSaldo | null }) {
  if (!s) return null;
  const { saldo, resultado, inicial } = numerosDoSaldo(s);
  // Sem saldo nao ha resultado; no celular a linha vira cartao e o rotulo vazio parecia defeito.
  if (!saldo) return <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Sem saldo</span>;
  if (!resultado) return <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Sem base pra calcular</span>;
  return <><span className={`mtc-mono mtc-corretora__resultado mtc-corretora__resultado--${resultado.tom}`}>{resultado.valor}</span>{inicial && <span className="mtc-tabela__apoio">sobre {inicial}</span>}</>;
}

/** Um valor de saldo inicial na lista de planos: "US$ 25.000,00" ou nada. */
export const saldoInicialDe = (p: { saldo_inicial_centavos: number | null; moeda: string | null } | undefined) => (p?.saldo_inicial_centavos != null ? formatarMoeda(p.saldo_inicial_centavos, p.moeda) : null);
export type { SaldoInicialDoPlano };
